/**
 * `/content/analytics` — Post-level analytics: clicks, attributed leads, bookings (C4).
 */
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useContentAnalytics } from "@/hooks/use-content";
import type { ContentAnalyticsItem } from "@/lib/api-client";
import { formatTimestamp } from "@/lib/formatters";

const STATUS_STYLES: Record<ContentAnalyticsItem["status"], string> = {
  draft: "bg-gray-100 text-gray-600",
  scheduled: "bg-blue-100 text-blue-700",
  publishing: "bg-yellow-100 text-yellow-700",
  published: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};

export function ContentAnalyticsPage() {
  const analytics = useContentAnalytics();

  const totals =
    analytics.data?.items.reduce(
      (acc, item) => ({
        clicks: acc.clicks + item.clicks,
        leads: acc.leads + item.leads,
        bookings: acc.bookings + item.bookings,
      }),
      { clicks: 0, leads: 0, bookings: 0 },
    ) ?? null;

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Content</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Analytics</h1>
        <p className="text-sm text-muted-foreground">
          Click-through, lead attribution, and booking performance for your posts.
        </p>
      </header>

      {/* Summary cards */}
      {analytics.isLoading && (
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      )}

      {totals && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-gray-200">
            <CardHeader className="pb-1 pt-4 px-4">
              <CardTitle className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Total Clicks
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-3xl font-bold text-gray-900">{totals.clicks}</p>
            </CardContent>
          </Card>
          <Card className="border-gray-200">
            <CardHeader className="pb-1 pt-4 px-4">
              <CardTitle className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Leads from Content
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-3xl font-bold text-gray-900">{totals.leads}</p>
            </CardContent>
          </Card>
          <Card className="border-gray-200">
            <CardHeader className="pb-1 pt-4 px-4">
              <CardTitle className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Bookings from Content
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-3xl font-bold text-gray-900">{totals.bookings}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {analytics.isLoading && (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      )}

      {analytics.isError && (
        <p className="text-sm text-destructive">Failed to load analytics.</p>
      )}

      {analytics.data && analytics.data.items.length === 0 && (
        <div className="flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-gray-200 p-12 text-center">
          <p className="text-sm text-muted-foreground">No posts yet.</p>
          <Link to="/content/posts/new" className="text-sm text-primary hover:underline">
            Create your first post →
          </Link>
        </div>
      )}

      {analytics.data && analytics.data.items.length > 0 && (
        <div className="flex flex-col gap-2">
          {/* Table header */}
          <div className="hidden sm:grid grid-cols-[1fr_80px_80px_80px_100px] gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b border-gray-100">
            <span>Post</span>
            <span className="text-right">Clicks</span>
            <span className="text-right">Leads</span>
            <span className="text-right">Bookings</span>
            <span className="text-right">Published</span>
          </div>
          {analytics.data.items
            .sort((a, b) => b.clicks - a.clicks)
            .map((item) => (
              <Link key={item.post_id} to={`/content/posts/${item.post_id}`}>
                <Card className="border-gray-200 hover:border-primary/40 transition-colors">
                  <CardContent className="p-4">
                    {/* Mobile layout */}
                    <div className="flex items-start justify-between gap-3 sm:hidden">
                      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold uppercase text-gray-500">
                            {item.platform}
                          </span>
                          <span
                            className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[item.status]}`}
                          >
                            {item.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-900 line-clamp-2">{item.body_preview}</p>
                        {item.published_at && (
                          <p className="text-xs text-muted-foreground">
                            {formatTimestamp(item.published_at)}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 text-right flex gap-3">
                        <div>
                          <p className="text-xl font-bold text-gray-900">{item.clicks}</p>
                          <p className="text-xs text-muted-foreground">clicks</p>
                        </div>
                        <div>
                          <p className="text-xl font-bold text-gray-900">{item.leads}</p>
                          <p className="text-xs text-muted-foreground">leads</p>
                        </div>
                        <div>
                          <p className="text-xl font-bold text-gray-900">{item.bookings}</p>
                          <p className="text-xs text-muted-foreground">bookings</p>
                        </div>
                      </div>
                    </div>

                    {/* Desktop table row */}
                    <div className="hidden sm:grid grid-cols-[1fr_80px_80px_80px_100px] gap-3 items-center">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold uppercase text-gray-500">
                            {item.platform}
                          </span>
                          <span
                            className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[item.status]}`}
                          >
                            {item.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-900 truncate max-w-lg">{item.body_preview}</p>
                      </div>
                      <p className="text-right text-xl font-bold text-gray-900">{item.clicks}</p>
                      <p className="text-right text-xl font-bold text-gray-900">{item.leads}</p>
                      <p className="text-right text-xl font-bold text-gray-900">{item.bookings}</p>
                      <p className="text-right text-xs text-muted-foreground">
                        {item.published_at ? formatTimestamp(item.published_at) : "—"}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
        </div>
      )}
    </div>
  );
}

export default ContentAnalyticsPage;
